import Label, { LabelVariant } from '@/components/elements/Label/Label';
import { BokadirektPayBadges, isBokadirektPayCheckout } from '@/helpers/placeHelperTS';
import { PlaceEmployee } from '@/types/state/shared';

const neutralBadgeClass = 'bg-bg_secondary_neutral';

export type PaymentMethodBadge = Extract<LabelVariant, 'klarna' | 'qliro' | 'walley'>;

const PAYMENT_METHOD_BADGES: Record<'KLARNA' | 'QLIRO' | 'WALLEY', PaymentMethodBadge> = {
  KLARNA: 'klarna',
  QLIRO: 'qliro',
  WALLEY: 'walley',
} as const;

type Props = {
  employee: PlaceEmployee;
  isSistaminuten: boolean;
  inModal?: boolean;
  /** From useBokadirektPayBadges; without them a Bokadirekt Pay employee gets no Klarna or Walley badge. */
  bokadirektPayBadges?: BokadirektPayBadges;
  paymentMethodBadges?: PaymentMethodBadge[];
};

const EmployeeBadges = ({
  employee,
  isSistaminuten,
  inModal = false,
  bokadirektPayBadges,
  paymentMethodBadges,
}: Props) => {
  const onlinePaymentForSistaminutenOnly = Boolean(employee?.about?.settings?.onlinePaymentForSistaminutenOnly);
  const hideOnlinePayments = !isSistaminuten && onlinePaymentForSistaminutenOnly;
  const isBokadirektPay = isBokadirektPayCheckout(employee);
  const acceptsKlarna = isBokadirektPay ? bokadirektPayBadges?.klarna : employee?.about?.settings?.hasKlarna;
  const acceptsQliro = !isBokadirektPay && !hideOnlinePayments && employee?.about?.settings?.useQliro;
  const hasCertificate = employee?.about?.certificates?.length || false;
  const acceptsGiftcard = !isSistaminuten && employee?.about?.settings?.acceptsGiftCard;
  const _paymentMethodBadges: PaymentMethodBadge[] = paymentMethodBadges || [
    ...(acceptsKlarna ? [PAYMENT_METHOD_BADGES.KLARNA] : []),
    ...(acceptsQliro ? [PAYMENT_METHOD_BADGES.QLIRO] : []),
    ...(isBokadirektPay && bokadirektPayBadges?.walley ? [PAYMENT_METHOD_BADGES.WALLEY] : []),
  ];
  const hasBadges = acceptsGiftcard || hasCertificate || _paymentMethodBadges.length > 0;

  if (!hasBadges) return null;

  return (
    <>
      {_paymentMethodBadges.length > 0 &&
        _paymentMethodBadges.map((badge) => (
          <Label key={badge} variant={badge} size="lg" icon={false} className={neutralBadgeClass} />
        ))}
      {hasCertificate && !inModal && (
        <a href="#certificates">
          <Label variant="certificate" icon={false} className={neutralBadgeClass} />
        </a>
      )}
      {hasCertificate && inModal && <Label variant="certificate" icon={false} className={neutralBadgeClass} />}
      {acceptsGiftcard && <Label variant="giftcard" icon={false} className={neutralBadgeClass} />}
    </>
  );
};

export default EmployeeBadges;
